import {isEqual} from 'lodash';
import {DateTime, Interval} from 'luxon';
import React, {memo, ReactNode, useCallback, useEffect, useState} from 'react';

import {Plural, Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {useRelativeDateTime} from './relativeDateTimeContext';

/*
 * Props.
 */

export enum RelativeDateTimeFormatsEnum {
  SHORT = 'SHORT',
  LONG = 'LONG',
  FULL = 'FULL',
}

export interface RelativeDateTimeFormatterProps {
  /** The date to format. */
  value: number;
  /** Format of date to display. */
  format?: RelativeDateTimeFormatsEnum;
  /** Do not refresh the time. */
  shouldNotRefresh?: boolean;
  /** Called when the time is refreshed. This should be stable using useCallback. */
  onRefresh?: Handler;
}

/*
 * Component.
 */

export const RelativeDateTimeFormatter = memo<RelativeDateTimeFormatterProps>((props) => {
  const {value, format, shouldNotRefresh, onRefresh} = props;
  const [distance, setDistance] = useState(() => findRelativeTimeDistance(value));

  const maybeUpdateDistance = useCallback(() => {
    setDistance((previousDistance) => {
      const currentDistance = findRelativeTimeDistance(value);
      if (isEqual(currentDistance, previousDistance)) {
        // If the distance hasn't changed, return the previous distance to save a render.
        return previousDistance;
      }

      // Re-render with the updated distance.
      return currentDistance;
    });
  }, [value]);

  /*
   * If needed, register for the date to be updated periodically.
   */

  const {registerRefresher} = useRelativeDateTime();
  useEffect(() => {
    maybeUpdateDistance();

    if (shouldNotRefresh) {
      return undefined;
    }

    return registerRefresher(() => {
      maybeUpdateDistance();
      if (onRefresh) {
        onRefresh();
      }
    });
  }, [shouldNotRefresh, registerRefresher, onRefresh, maybeUpdateDistance]);

  /*
   * Render.
   */

  const formatToUse = format || RelativeDateTimeFormatsEnum.SHORT;
  const dateRender = renderersByFormat[formatToUse][distance.format](distance.value, distance.isFuture);

  return <>{dateRender}</>;
});

/*
 * Time logic.
 */

/*
 * | Distance between dates                                            | Result              |
 * |-------------------------------------------------------------------|---------------------|
 * | 0 ... 30 secs                                                     | now                 |
 * | 30 secs ... 44 mins 30 secs                                       | [1..44]m            |
 * | 44 mins 30 secs ... 23 hrs 59 mins 30 secs                        | [1..24]h            |
 * | 23 hrs 59 mins 30 secs ... 29 days 23 hrs 59 mins 30 secs         | [1..30]d            |
 * | 29 days 23 hrs 59 mins 30 secs ... 1 yr                           | [1..12]mth          |
 * | 1 yr ... 1 yr 9 months                                            | 1y                  |
 * | N yrs 9 months ... N+1 yrs 9 months                               | (N+1)y              |
 */

export enum TimeDistanceFormatsEnum {
  NOW = 'NOW',
  MINUTE = 'MINUTE',
  HOUR = 'HOUR',
  DAY = 'DAY',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export interface RelativeTimeDistance {
  format: TimeDistanceFormatsEnum;
  value: number;
  isFuture: boolean;
}

export function findRelativeTimeDistance(fromDate: number): RelativeTimeDistance {
  // Compute the duration from now.
  const isFuture = fromDate > Date.now();
  const interval = getInterval(DateTime.fromMillis(fromDate), DateTime.local());

  // Now.
  const minutesLength = interval.length('minutes');
  if (minutesLength <= 0.6) {
    return {format: TimeDistanceFormatsEnum.NOW, value: Math.round(interval.length('seconds')), isFuture};
  }

  // Minutes.
  const hoursLength = interval.length('hours');
  if (hoursLength <= 0.75) {
    return {format: TimeDistanceFormatsEnum.MINUTE, value: Math.round(minutesLength), isFuture};
  }

  // Hours.
  const daysLength = interval.length('days');
  if (daysLength <= 0.8) {
    return {format: TimeDistanceFormatsEnum.HOUR, value: Math.round(hoursLength), isFuture};
  }

  // Days.
  const monthsLength = interval.length('months');
  if (monthsLength <= 0.8) {
    return {format: TimeDistanceFormatsEnum.DAY, value: Math.round(daysLength), isFuture};
  }

  // Months.
  const yearsLength = interval.length('years');
  if (yearsLength <= 0.8) {
    return {format: TimeDistanceFormatsEnum.MONTH, value: Math.round(monthsLength), isFuture};
  }

  // Years.
  return {format: TimeDistanceFormatsEnum.YEAR, value: Math.round(yearsLength), isFuture};
}

function getInterval(date1: DateTime, date2: DateTime) {
  // If the dates are the correct order, build the interval.
  if (date2 > date1) {
    return Interval.fromDateTimes(date1, date2);
  }

  // Otherwise, invert first.
  return Interval.fromDateTimes(date2, date1);
}

/*
 * Localized renderers.
 */

type TimeDistanceRenderers = {
  [T in TimeDistanceFormatsEnum]: (value: number, isFuture: boolean) => ReactNode;
};
// prettier-ignore
const renderersShort: TimeDistanceRenderers = {
  [TimeDistanceFormatsEnum.NOW]: () => <Trans id="time-now">now</Trans>,
  [TimeDistanceFormatsEnum.MINUTE]: value => <Trans id="time-minute">{value}m</Trans>,
  [TimeDistanceFormatsEnum.HOUR]: value => <Trans id="time-hour">{value}h</Trans>,
  [TimeDistanceFormatsEnum.DAY]: value => <Trans id="time-day">{value}d</Trans>,
  [TimeDistanceFormatsEnum.MONTH]: value => <Trans id="time-month">{value}mth</Trans>,
  [TimeDistanceFormatsEnum.YEAR]: value => <Trans id="time-year">{value}y</Trans>
};

// prettier-ignore
const renderersLong: TimeDistanceRenderers = {
  [TimeDistanceFormatsEnum.NOW]: () => <Trans id="time-now">now</Trans>,
  [TimeDistanceFormatsEnum.MINUTE]: value => <Plural id="time-long-minute" value={value} one="# minute" other="# minutes" />,
  [TimeDistanceFormatsEnum.HOUR]: value => <Plural id="time-long-hour" value={value} one="# hour" other="# hours" />,
  [TimeDistanceFormatsEnum.DAY]: value => <Plural id="time-long-day" value={value} one="# day" other="# days" />,
  [TimeDistanceFormatsEnum.MONTH]: value => <Plural id="time-long-month" value={value} one="# month" other="# months" />,
  [TimeDistanceFormatsEnum.YEAR]: value => <Plural id="time-long-year" value={value} one="# year" other="# years" />
};

const renderersWithText: TimeDistanceRenderers = {
  [TimeDistanceFormatsEnum.NOW]: () => <Trans id="time-just-now">just now</Trans>,
  [TimeDistanceFormatsEnum.MINUTE]: (value, isFuture) =>
    fullText(TimeDistanceFormatsEnum.MINUTE, value, isFuture),
  [TimeDistanceFormatsEnum.HOUR]: (value, isFuture) =>
    fullText(TimeDistanceFormatsEnum.HOUR, value, isFuture),
  [TimeDistanceFormatsEnum.DAY]: (value, isFuture) => fullText(TimeDistanceFormatsEnum.DAY, value, isFuture),
  [TimeDistanceFormatsEnum.MONTH]: (value, isFuture) =>
    fullText(TimeDistanceFormatsEnum.MONTH, value, isFuture),
  [TimeDistanceFormatsEnum.YEAR]: (value, isFuture) =>
    fullText(TimeDistanceFormatsEnum.YEAR, value, isFuture),
};

type TimeDistanceRenderersByFormat = {[T in RelativeDateTimeFormatsEnum]: TimeDistanceRenderers};
const renderersByFormat: TimeDistanceRenderersByFormat = {
  [RelativeDateTimeFormatsEnum.SHORT]: renderersShort,
  [RelativeDateTimeFormatsEnum.LONG]: renderersLong,
  [RelativeDateTimeFormatsEnum.FULL]: renderersWithText,
};

function fullText(format: TimeDistanceFormatsEnum, value: number, isFuture: boolean) {
  if (isFuture) {
    return <Trans id="time-in">in {renderersLong[format](value, isFuture)}</Trans>;
  }

  return <Trans id="time-ago">{renderersLong[format](value, isFuture)} ago</Trans>;
}
